import { createContext, type ReactNode, useContext, useMemo } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'

/**
 * Survey-form context only: the id the survey page flows read. Layout/theming
 * data lives in SurveyLayout_survey. Do NOT grow this fragment with layout/UI
 * fields; colocate a fragment on the consuming component instead.
 */
const fragments = {
  SurveyProvider_survey: graphql(`
    fragment SurveyProvider_survey on PublicSurvey {
      id
    }
  `),
}

export type Survey = {
  /** the survey's report source global id (a PublicReportSourceGlobalId); the
   * page queries pass it as both the reportSourceId arg and the channelIdHack arg */
  surveyId: string
}

const SurveyContext = createContext<Survey | null>(null)

type SurveyProviderProps = {
  children: ReactNode
  survey: FragmentType<typeof fragments.SurveyProvider_survey>
}

export const SurveyProvider = ({ children, survey: _survey }: SurveyProviderProps) => {
  const survey = getFragmentData(fragments.SurveyProvider_survey, _survey)

  const value = useMemo<Survey>(() => ({ surveyId: survey.id }), [survey])

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>
}

export const useSurvey = () => {
  const survey = useContext(SurveyContext)
  if (!survey) {
    throw new Error('useSurvey must be used within a SurveyProvider')
  }
  return survey
}
